import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ApiError, api, apiErrorCode, uploadVideo, type ResumableUploadTask } from '../api';
import type { Folder, HostedEntitlement, UploadItem, UploadSession, VideoItem } from '../types';
import { flattenFolders, formatBytes, formatQuotaBytes } from '../utils';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { QuotaHelp, quotaResourceForError } from './QuotaHelp';
import { AccordionItem } from './AccordionItem';

type UploadMode = 'files' | 'hls';
type NotificationTone = 'success' | 'error' | 'info';
type ProtectionMode = 'r2_only' | 'protected';

const DEFAULT_PUBLICATION_BYTES_PER_HOUR = 4_700_000_000;

function fileIdentity(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

export function isImageUpload(file: File): boolean {
  return ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name);
}

async function videoDuration(file: File): Promise<number | null> {
  if (typeof URL.createObjectURL !== 'function' || typeof URL.revokeObjectURL !== 'function') return null;
  return await new Promise((resolve) => {
    const element = document.createElement('video');
    let url = '';
    try {
      url = URL.createObjectURL(file);
    } catch {
      resolve(null);
      return;
    }
    const finish = (value: number | null) => {
      URL.revokeObjectURL(url);
      element.removeAttribute('src');
      resolve(value);
    };
    element.preload = 'metadata';
    element.onloadedmetadata = () => finish(Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null);
    element.onerror = () => finish(null);
    element.src = url;
  });
}

interface UploadModalProps {
  open: boolean;
  folderId: string | null;
  folderName: string;
  folders: Folder[];
  hostedEntitlementsEnabled?: boolean;
  imageHostingEnabled?: boolean;
  onClose: () => void;
  onUploaded: (message?: string, tone?: NotificationTone) => void;
}

function importSourceError(value: string): string {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== 'https:') return 'Use an HTTPS URL.';
    if (url.username || url.password || url.port || url.search || url.hash) return 'Use an Adilo URL without credentials, a custom port, query, or fragment.';
    if (host !== 'adilo.bigcommand.com') return 'Enter an adilo.bigcommand.com HLS URL.';
    if (!/^\/[A-Za-z0-9_-]{8,128}\.m3u8$/.test(url.pathname)) return 'Enter an Adilo share URL ending in .m3u8.';
    return '';
  } catch {
    return 'Enter a valid Adilo HLS URL.';
  }
}

function thumbnailUrlError(value: string): string {
  if (!value) return '';
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && !url.port && !url.hash
      ? ''
      : 'Use an HTTPS thumbnail URL without credentials, a custom port, or fragment.';
  } catch {
    return 'Enter a valid thumbnail URL.';
  }
}

function formatUploadDate(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function friendlyImportStage(video: VideoItem): string {
  if (video.status === 'ready') return 'Import complete';
  if (video.status === 'failed') return 'Import failed';
  const stage = video.processingStage ?? video.status;
  const labels: Record<string, string> = {
    queued: 'Waiting to start',
    inspecting_manifest: 'Inspecting manifest',
    discovering_renditions: 'Discovering renditions',
    downloading_playlists: 'Downloading playlists',
    downloading_segments: 'Downloading segments',
    validating_media: 'Validating media',
    complete: 'Import complete',
    failed: 'Import failed',
    resolving: 'Connecting to Adilo',
    resolving_source: 'Connecting to Adilo',
    fetching_manifest: 'Reading the HLS manifest',
    downloading_manifest: 'Reading the HLS manifest',
    validating_manifest: 'Checking HLS compatibility',
    downloading: 'Copying HLS media',
    copying: 'Copying HLS media',
    copying_segments: 'Copying HLS media',
    importing: 'Copying HLS media',
    rewriting: 'Preparing local playlists',
    rewriting_playlists: 'Preparing local playlists',
    thumbnail: 'Preparing the thumbnail',
    validating: 'Validating the imported video',
    publishing: 'Publishing the video',
    finalizing: 'Publishing the video',
  };
  return labels[stage] ?? stage.replace(/[_-]+/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
}

export function UploadModal({ open, folderId, folderName, folders, hostedEntitlementsEnabled = false, imageHostingEnabled = false, onClose, onUploaded }: UploadModalProps) {
  const [mode, setMode] = useState<UploadMode>('files');
  const [files, setFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const [uploadHelpOpen, setUploadHelpOpen] = useState(false);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [running, setRunning] = useState(false);
  const [recoveringUploads, setRecoveringUploads] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [decidingUploadId, setDecidingUploadId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [importTitle, setImportTitle] = useState('');
  const [importFolderId, setImportFolderId] = useState<string | null>(folderId);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importErrorCode, setImportErrorCode] = useState('');
  const [pollError, setPollError] = useState('');
  const [importedVideo, setImportedVideo] = useState<VideoItem | null>(null);
  const [hostedEntitlement, setHostedEntitlement] = useState<HostedEntitlement | null>(null);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [entitlementError, setEntitlementError] = useState('');
  const [protectionMode, setProtectionMode] = useState<ProtectionMode>('protected');
  const [fileDurations, setFileDurations] = useState<Record<string, number>>({});
  const taskRef = useRef<ResumableUploadTask | null>(null);
  const imageAbortRef = useRef<AbortController | null>(null);
  const activeItemRef = useRef<string | null>(null);
  const itemsRef = useRef<UploadItem[]>([]);
  const localFilesRef = useRef(new Map<string, File>());
  const nextItemIdRef = useRef(0);
  const processingRef = useRef(false);
  const stoppedRef = useRef(false);
  const cancelledRef = useRef(new Set<string>());
  const folderOptions = useMemo(() => flattenFolders(folders), [folders]);

  useEffect(() => {
    if (open && !importedVideo) setImportFolderId(folderId);
  }, [folderId, importedVideo, open]);

  useEffect(() => {
    if (open) return;
    stoppedRef.current = true;
    processingRef.current = false;
    void taskRef.current?.park();
    imageAbortRef.current?.abort();
    taskRef.current = null;
    activeItemRef.current = null;
    itemsRef.current = [];
    localFilesRef.current.clear();
    cancelledRef.current.clear();
    setMode('files');
    setFiles([]);
    setRejectedFiles([]);
    setUploadHelpOpen(false);
    setItems([]);
    setRunning(false);
    setRecoveringUploads(false);
    setRecoveryError('');
    setDecidingUploadId(null);
    setDragging(false);
    setSourceUrl('');
    setImportTitle('');
    setImportFolderId(folderId);
    setThumbnailUrl('');
    setImporting(false);
    setImportError('');
    setImportErrorCode('');
    setPollError('');
    setImportedVideo(null);
    setHostedEntitlement(null);
    setEntitlementLoading(false);
    setEntitlementError('');
    setProtectionMode('protected');
    setFileDurations({});
  }, [folderId, open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setRecoveringUploads(true);
    setRecoveryError('');
    void api.pendingUploadSessions().then((sessions) => {
      if (!active) return;
      mergePendingUploads(sessions);
      setRecoveringUploads(false);
    }, () => {
      if (!active) return;
      setRecoveryError('Unable to check for uploads awaiting duplicate review. Try again before starting another upload.');
      setRecoveringUploads(false);
    });
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open || !hostedEntitlementsEnabled) {
      setHostedEntitlement(null);
      setEntitlementLoading(false);
      setEntitlementError('');
      return;
    }
    let active = true;
    setEntitlementLoading(true);
    setEntitlementError('');
    void api.hostedEntitlement().then((entitlement) => {
      if (!active) return;
      setHostedEntitlement(entitlement);
      setEntitlementLoading(false);
    }, () => {
      if (!active) return;
      setHostedEntitlement(null);
      setEntitlementLoading(false);
      setEntitlementError('Unable to load your current storage allowance. Try reopening this window before adding a video.');
    });
    return () => { active = false; };
  }, [hostedEntitlementsEnabled, open]);

  const publicationRate = hostedEntitlement?.publicationEstimateBytesPerHour ?? DEFAULT_PUBLICATION_BYTES_PER_HOUR;
  const fileEstimateBytes = useMemo(() => files.reduce((total, file) => {
    // Match the server's temporary derivative reservation, not an invented
    // compression ratio. Actual usage is reconciled after optimization.
    if (imageHostingEnabled && isImageUpload(file)) return total + file.size + 24 * 1024 ** 2;
    const duration = fileDurations[fileIdentity(file)] ?? hostedEntitlement?.sourceMaxDurationSeconds ?? 0;
    return total + Math.ceil(publicationRate * (duration / 3600));
  }, 0), [fileDurations, files, hostedEntitlement?.sourceMaxDurationSeconds, publicationRate, imageHostingEnabled]);
  const protectedEstimateBytes = mode === 'files' ? files.reduce((total, file) => total + (imageHostingEnabled && isImageUpload(file) ? file.size + 65536 : Math.ceil(publicationRate * ((fileDurations[fileIdentity(file)] ?? hostedEntitlement?.sourceMaxDurationSeconds ?? 0) / 3600))), 0) : fileEstimateBytes;
  const importEstimateBytes = hostedEntitlement
    ? Math.ceil(publicationRate * (hostedEntitlement.sourceMaxDurationSeconds / 3600))
    : 0;
  const selectedEstimateBytes = mode === 'files' ? fileEstimateBytes : importEstimateBytes;
  const r2Unavailable = Boolean(hostedEntitlement && selectedEstimateBytes > hostedEntitlement.r2Delivery.availableBytes);
  const protectedUnavailable = Boolean(hostedEntitlement && (
    r2Unavailable || (mode === 'files' ? protectedEstimateBytes : selectedEstimateBytes) > hostedEntitlement.protectedArchive.availableBytes
  ));
  const entitlementUnavailable = hostedEntitlementsEnabled
    && (entitlementLoading || Boolean(entitlementError) || !hostedEntitlement);
  const quotaResources = new Set(items.filter((item) => item.state === 'failed')
    .map((item) => quotaResourceForError(item.errorCode ?? '')).filter((resource) => resource !== null));
  const importQuotaResource = importError ? quotaResourceForError(importErrorCode) : null;
  if (importQuotaResource) quotaResources.add(importQuotaResource);
  if (!items.length && !importedVideo && r2Unavailable) quotaResources.add('storage');

  async function refreshAllowance() {
    try { setHostedEntitlement(await api.hostedEntitlement()); setEntitlementError(''); } catch {
      setEntitlementError('Unable to refresh your allowance. Please try again.');
    }
  }

  useEffect(() => {
    if (protectedUnavailable && !r2Unavailable) setProtectionMode('r2_only');
  }, [protectedUnavailable, r2Unavailable]);

  useEffect(() => {
    if (!open || !importedVideo || ['ready', 'failed'].includes(importedVideo.status)) return;
    let active = true;
    let timer = 0;

    const poll = async () => {
      try {
        const video = await api.video(importedVideo.id);
        if (!active) return;
        setImportedVideo(video);
        setPollError('');
        if (video.status === 'ready') {
          onUploaded('HLS import complete. The video is ready.', 'success');
          return;
        }
        if (video.status === 'failed') {
          onUploaded(video.errorMessage || 'The HLS import failed.', 'error');
          return;
        }
      } catch {
        if (!active) return;
        setPollError('Unable to check the import right now. Retrying automatically…');
      }
      timer = window.setTimeout(() => void poll(), 2_000);
    };

    timer = window.setTimeout(() => void poll(), 2_000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [importedVideo?.id, importedVideo?.status, onUploaded, open]);

  function addFiles(selected: FileList | File[]) {
    const selectedFiles = Array.from(selected);
    const accepted = selectedFiles.filter((file) => file.type.startsWith('video/') || /\.(mp4|m4v|mov|mkv|webm|avi|mpeg|mpg|ts)$/i.test(file.name) || imageHostingEnabled && isImageUpload(file));
    setRejectedFiles(current => [...current, ...selectedFiles.filter(file => !accepted.includes(file)).map(file => `${file.name}: unsupported format. Choose a video${imageHostingEnabled ? ', JPEG, PNG, or static WebP' : ''}.`)]);
    setFiles((current) => {
      const known = new Set(current.map((file) => `${file.name}-${file.size}-${file.lastModified}`));
      return [...current, ...accepted.filter((file) => !known.has(`${file.name}-${file.size}-${file.lastModified}`))];
    });
    for (const file of accepted) {
      if (imageHostingEnabled && isImageUpload(file)) continue;
      void videoDuration(file).then((duration) => {
        if (duration) setFileDurations((current) => ({ ...current, [fileIdentity(file)]: duration }));
      });
    }
  }

  function replaceItems(update: (current: UploadItem[]) => UploadItem[]) {
    const next = update(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
  }

  function updateItem(id: string, changes: Partial<UploadItem>) {
    replaceItems((current) => current.map((item) => item.id === id ? { ...item, ...changes } : item));
  }

  function mergePendingUploads(sessions: UploadSession[]) {
    replaceItems((current) => {
      const next = [...current];
      for (const uploadSession of sessions) {
        const existingIndex = next.findIndex((item) => item.uploadSession?.id === uploadSession.id);
        const recovered: UploadItem = {
          id: existingIndex >= 0 ? next[existingIndex]!.id : `upload-session-${uploadSession.id}`,
          fileName: uploadSession.filename,
          progress: 100,
          state: 'duplicate',
          uploadSession,
        };
        if (existingIndex >= 0) next[existingIndex] = { ...next[existingIndex]!, ...recovered };
        else next.push(recovered);
      }
      return next;
    });
  }

  async function retryPendingRecovery() {
    if (recoveringUploads) return;
    setRecoveringUploads(true);
    setRecoveryError('');
    try {
      mergePendingUploads(await api.pendingUploadSessions());
    } catch {
      setRecoveryError('Unable to check for uploads awaiting duplicate review. Try again before starting another upload.');
    } finally {
      setRecoveringUploads(false);
    }
  }

  async function runUpload(file: File, item: UploadItem): Promise<'complete' | 'duplicate' | 'failed'> {
    activeItemRef.current = item.id;
    updateItem(item.id, { state: 'uploading', error: undefined, errorCode: undefined });
    if (imageHostingEnabled && isImageUpload(file)) {
      const controller = new AbortController();
      imageAbortRef.current = controller;
      try {
        if (file.size > 32 * 1024 * 1024) throw new Error('This image exceeds the 32 MiB upload limit. Choose a smaller original.');
        await api.uploadImage(file, folderId, hostedEntitlement ? protectionMode : 'automatic', controller.signal);
        updateItem(item.id, { state: 'complete', progress: 100 });
        return 'complete';
      } catch (reason) {
        updateItem(item.id, { state: controller.signal.aborted ? 'cancelled' : 'failed', error: controller.signal.aborted ? undefined : reason instanceof Error ? reason.message : 'Image upload failed. Select Retry to upload it again.', errorCode: apiErrorCode(reason) });
        return 'failed';
      } finally { imageAbortRef.current = null; activeItemRef.current = null; }
    }
    const task = uploadVideo(file, folderId, (progress) => updateItem(item.id, { progress }), {
      protectionMode: hostedEntitlement ? protectionMode : 'automatic',
      sourceDurationSeconds: fileDurations[fileIdentity(file)] ?? hostedEntitlement?.sourceMaxDurationSeconds ?? null,
    });
    taskRef.current = task;
    task.start();
    try {
      const { uploadSession } = await task.promise;
      if (uploadSession.status === 'awaiting_duplicate_decision') {
        updateItem(item.id, { progress: 100, state: 'duplicate', uploadSession, error: undefined });
        return 'duplicate';
      }
      if (uploadSession.status === 'completed') {
        updateItem(item.id, { progress: 100, state: 'complete', uploadSession, error: undefined });
        return 'complete';
      }
      updateItem(item.id, {
        progress: 100,
        state: 'failed',
        uploadSession,
        error: `The server reported the upload as “${uploadSession.status || 'unknown'}” instead of completing it.`,
      });
      return 'failed';
    } catch (reason) {
      if (cancelledRef.current.has(item.id)) {
        updateItem(item.id, { state: 'cancelled', error: undefined });
      } else if (!stoppedRef.current) {
        const error = reason instanceof ApiError ? reason.message : 'Upload failed after automatic retries.';
        updateItem(item.id, { state: 'failed', error, errorCode: apiErrorCode(reason) });
      }
      return 'failed';
    } finally {
      taskRef.current = null;
      activeItemRef.current = null;
    }
  }

  async function processQueue() {
    if (processingRef.current || stoppedRef.current || itemsRef.current.some((item) => item.state === 'duplicate')) return;
    processingRef.current = true;
    setRunning(true);
    let completed = 0;
    try {
      while (!stoppedRef.current && !itemsRef.current.some((item) => item.state === 'duplicate')) {
        const item = itemsRef.current.find((candidate) => candidate.state === 'waiting');
        if (!item) break;
        const file = localFilesRef.current.get(item.id);
        if (!file) {
          updateItem(item.id, { state: 'failed', error: 'The local file is no longer available. Select it again to resume.' });
          continue;
        }
        const outcome = await runUpload(file, item);
        if (outcome === 'complete') completed += 1;
        if (outcome === 'duplicate') break;
      }
    } finally {
      processingRef.current = false;
      setRunning(false);
      if (completed > 0 && !stoppedRef.current) onUploaded(imageHostingEnabled ? 'Upload complete. Media is queued for processing and remains private.' : 'Upload complete. Video is queued for processing.');
    }
  }

  async function startUploads() {
    if (!files.length || running || processingRef.current || recoveringUploads || recoveryError
      || entitlementUnavailable || itemsRef.current.some((item) => item.state === 'duplicate')) return;
    stoppedRef.current = false;
    cancelledRef.current.clear();
    const queue = files.map((file) => {
      const id = `local-upload-${nextItemIdRef.current++}`;
      localFilesRef.current.set(id, file);
      return { id, fileName: file.name, progress: 0, state: 'waiting' as const };
    });
    itemsRef.current = queue;
    setItems(queue);
    await processQueue();
  }

  async function retryUpload(item: UploadItem) {
    if (running || !localFilesRef.current.has(item.id)) return;
    stoppedRef.current = false;
    cancelledRef.current.delete(item.id);
    updateItem(item.id, { state: 'waiting', error: undefined });
    await processQueue();
  }

  async function decideDuplicate(item: UploadItem, decision: 'continue' | 'cancel') {
    const uploadSession = item.uploadSession;
    if (!uploadSession || decidingUploadId) return;
    setDecidingUploadId(uploadSession.id);
    updateItem(item.id, { error: undefined });
    try {
      const decidedSession = await api.decideUploadDuplicate(uploadSession.id, decision);
      updateItem(item.id, {
        progress: 100,
        state: decision === 'continue' ? 'complete' : 'cancelled',
        uploadSession: decidedSession,
        error: undefined,
      });
      if (decision === 'continue') onUploaded('Another copy was accepted and is queued for processing.', 'success');
      if (!stoppedRef.current) await processQueue();
    } catch (reason) {
      const error = reason instanceof ApiError ? reason.message : 'Unable to save this duplicate upload decision. Please try again.';
      updateItem(item.id, { state: 'duplicate', error });
    } finally {
      setDecidingUploadId(null);
    }
  }

  async function pauseActive() {
    const id = activeItemRef.current;
    if (id && imageAbortRef.current) { updateItem(id, { error: 'Images cannot be paused. Cancel this upload and select Retry to upload it again.' }); return; }
    if (!id || !taskRef.current) return;
    try {
      await taskRef.current.pause();
      updateItem(id, { state: 'paused' });
    } catch {
      updateItem(id, { state: 'failed', error: 'Unable to pause this upload.' });
    }
  }

  function resumeActive() {
    const id = activeItemRef.current;
    if (!id || !taskRef.current) return;
    updateItem(id, { state: 'uploading' });
    taskRef.current.start();
  }

  async function cancelActive() {
    const id = activeItemRef.current;
    if (!id) return;
    if (imageAbortRef.current) {
      if (!window.confirm('Cancel this image upload? Any incomplete source will remain unavailable.')) return;
      cancelledRef.current.add(id); imageAbortRef.current.abort(); return;
    }
    if (!taskRef.current) return;
    if (!window.confirm('Cancel this upload and remove its saved server chunks?')) return;
    cancelledRef.current.add(id);
    updateItem(id, { state: 'cancelled', error: undefined });
    await taskRef.current.cancel();
  }

  async function submitImport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (importing || importedVideo || entitlementUnavailable) return;
    const sourceError = importSourceError(sourceUrl.trim());
    const thumbnailError = thumbnailUrlError(thumbnailUrl.trim());
    if (sourceError || thumbnailError) {
      setImportError(sourceError || thumbnailError);
      return;
    }

    setImporting(true);
    setImportError('');
    try {
      const video = await api.importHls({
        sourceUrl: sourceUrl.trim(),
        title: importTitle.trim() || undefined,
        folderId: importFolderId,
        thumbnailUrl: thumbnailUrl.trim() || undefined,
        protectionMode: hostedEntitlement ? protectionMode : 'automatic',
      });
      setImportedVideo(video);
      if (video.status === 'ready') onUploaded('HLS import complete. The video is ready.', 'success');
      else if (video.status === 'failed') onUploaded(video.errorMessage || 'The HLS import failed.', 'error');
      else onUploaded('HLS import accepted. It will continue if you close this window.', 'info');
    } catch (reason) {
      setImportError(reason instanceof ApiError ? reason.message : 'The HLS import could not be started.');
      setImportErrorCode(apiErrorCode(reason));
    } finally {
      setImporting(false);
    }
  }

  function handleClose() {
    if (running && !window.confirm(imageAbortRef.current ? 'Cancel the current image upload and close? Images cannot resume; select the file again to retry.' : 'Pause the current upload and close? You can resume by selecting the same file again.')) return;
    stoppedRef.current = true;
    const task = taskRef.current;
    taskRef.current = null;
    void task?.park();
    imageAbortRef.current?.abort();
    onClose();
  }

  const hasPendingReview = items.some((item) => item.state === 'duplicate');
  const fileFooter = <>
    <button type="button" className="button button--secondary" onClick={handleClose}>{running ? imageAbortRef.current ? 'Cancel image & close' : 'Pause & close' : items.length ? 'Close' : 'Cancel'}</button>
    {!items.length && <button type="button" className="button button--primary" onClick={() => void startUploads()} disabled={!files.length || recoveringUploads || Boolean(recoveryError) || entitlementUnavailable || r2Unavailable || (protectionMode === 'protected' && protectedUnavailable)}><Icon name="upload" size={18} />Upload {files.length || ''} {imageHostingEnabled ? files.length === 1 ? 'file' : 'files' : files.length === 1 ? 'video' : 'videos'}</button>}
  </>;
  const importFooter = <>
    <button type="button" className="button button--secondary" onClick={handleClose}>{importedVideo ? 'Close' : 'Cancel'}</button>
    {!importedVideo && <button type="submit" form="hls-import-form" className="button button--primary" disabled={importing || entitlementUnavailable || r2Unavailable || (protectionMode === 'protected' && protectedUnavailable)}>{importing ? <span className="spinner" /> : <Icon name="plus" size={18} />}Import HLS</button>}
  </>;

  return <Modal open={open} title={imageHostingEnabled ? 'Add media' : 'Add videos'} description={mode === 'files' ? `Add ${imageHostingEnabled ? 'images and videos' : 'videos'} to ${folderName}.` : `Import a compatible Adilo HLS video to ${folderName}.`} onClose={handleClose} width="medium" footer={mode === 'files' ? fileFooter : importFooter}>
    <div className="upload-modes" role="tablist" aria-label="Add video method">
      <button type="button" role="tab" aria-selected={mode === 'files'} className={mode === 'files' ? 'is-active' : ''} onClick={() => setMode('files')} disabled={running || importing || Boolean(importedVideo)}>Upload files</button>
      <button type="button" role="tab" aria-selected={mode === 'hls'} className={mode === 'hls' ? 'is-active' : ''} onClick={() => setMode('hls')} disabled={running || importing || Boolean(importedVideo) || hasPendingReview}>Import HLS URL</button>
    </div>

    {hostedEntitlementsEnabled && entitlementLoading && !items.length && !importedVideo && <p className="upload-recovery-status" role="status">Loading your storage allowance…</p>}
    {entitlementError && !items.length && !importedVideo && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{entitlementError}</p>}
    {hostedEntitlement && !items.length && !importedVideo && <fieldset className="publication-protection">
      <legend>Storage</legend>
      <p className="publication-protection__intro">Choose once. Storage mode cannot be changed later.</p>
      <div className="publication-protection__choices">
        <label className={`publication-protection__choice ${protectionMode === 'r2_only' ? 'is-selected' : ''} ${r2Unavailable ? 'is-disabled' : ''}`}>
          <input className="control-choice" type="radio" name="publication-protection" value="r2_only" checked={protectionMode === 'r2_only'} disabled={r2Unavailable} onChange={() => setProtectionMode('r2_only')} />
          <span><strong>Playback storage</strong><small className="publication-protection__availability"><Icon name="hard-drive" size={14} />{formatQuotaBytes(hostedEntitlement.r2Delivery.availableBytes)} available</small></span>
        </label>
        <label className={`publication-protection__choice ${protectionMode === 'protected' ? 'is-selected' : ''} ${protectedUnavailable ? 'is-disabled' : ''}`}>
          <input className="control-choice" type="radio" name="publication-protection" value="protected" checked={protectionMode === 'protected'} disabled={protectedUnavailable} onChange={() => setProtectionMode('protected')} />
          <span><strong>Protected storage</strong><small className="publication-protection__availability"><Icon name="shield" size={14} />{formatQuotaBytes(hostedEntitlement.protectedArchive.availableBytes)} available</small></span>
        </label>
      </div>
      {r2Unavailable && <p className="publication-protection__warning" role="alert">{imageHostingEnabled ? 'The selected media needs more temporary processing space than your remaining playback-storage quota.' : 'The estimated HLS output exceeds the remaining playback-storage quota.'}</p>}
      {!r2Unavailable && protectedUnavailable && <p className="publication-protection__warning" role="status">Protected storage is unavailable for {imageHostingEnabled ? 'these originals' : 'this video'}; playback storage is still available.</p>}
    </fieldset>}

    {hostedEntitlementsEnabled && [...quotaResources].map((resource) => <QuotaHelp key={resource} resource={resource} onRefresh={refreshAllowance} />)}

    {mode === 'files' ? !items.length ? <>
      {recoveryError && <div className="upload-recovery-error" role="alert"><span>{recoveryError}</span><button type="button" onClick={() => void retryPendingRecovery()} disabled={recoveringUploads}>{recoveringUploads ? 'Checking…' : 'Try again'}</button></div>}
      {recoveringUploads && !recoveryError && <p className="upload-recovery-status" role="status">Checking for uploads awaiting duplicate review…</p>}
      <label className={`drop-zone ${dragging ? 'is-dragging' : ''}`} onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}>
        <input type="file" accept={imageHostingEnabled ? 'video/*,.mkv,.m4v,.avi,.mpeg,.mpg,.ts,image/jpeg,image/png,image/webp' : 'video/*,.mkv,.m4v,.avi,.mpeg,.mpg,.ts'} multiple onChange={(event) => event.target.files && addFiles(event.target.files)} />
        <span className="drop-zone__icon"><Icon name="upload" size={25} /></span>
        <strong>{imageHostingEnabled ? 'Drop images and videos here' : 'Drop video files here'}</strong><span>or <u>browse your computer</u></span>
        {!imageHostingEnabled && <span className="drop-zone__meta">
          {hostedEntitlement && <><span><Icon name="hard-drive" size={14} />Max {formatQuotaBytes(hostedEntitlement.sourceMaxBytes)}</span><span><Icon name="film" size={14} />{Math.round(hostedEntitlement.sourceMaxDurationSeconds / 60)} min</span></>}
          <span><Icon name="refresh" size={14} />Resumable</span>
        </span>}
      </label>
      {imageHostingEnabled && <section className="upload-file-guidance" aria-label="Media upload limits">
        <div className="upload-file-guidance__columns">
          <section aria-label="Image upload limits">
            <h3>Images</h3>
            <dl><div><dt>Formats</dt><dd>JPEG, PNG, static WebP</dd></div><div><dt>Per image</dt><dd>Up to 32 MiB · 24 million pixels</dd></div><div><dt>Processing</dt><dd>No video processing minutes used</dd></div></dl>
          </section>
          <section aria-label="Video upload limits">
            <h3>Videos</h3>
            <dl>
              {hostedEntitlement && <><div><dt>Per video</dt><dd>Up to {formatQuotaBytes(hostedEntitlement.sourceMaxBytes)}</dd></div><div><dt>Duration</dt><dd>Up to {Math.round(hostedEntitlement.sourceMaxDurationSeconds / 60)} min</dd></div></>}
              <div><dt>Uploads</dt><dd>Resumable</dd></div>
            </dl>
          </section>
        </div>
        <AccordionItem title="Storage and interrupted uploads" open={uploadHelpOpen} onToggle={() => setUploadHelpOpen(value => !value)}>
          <div className="form-help"><p>Each image temporarily reserves its original plus 24 MiB for display sizes. After optimization, only actual stored bytes count toward storage.</p><p>Videos can resume. If an image upload is interrupted, select the file again to retry.</p></div>
        </AccordionItem>
      </section>}
      {rejectedFiles.length > 0 && <div className="form-alert" role="alert">{rejectedFiles.map((message, index) => <p key={`${index}-${message}`}>{message}</p>)}</div>}
      {files.length > 0 && <div className="selected-files"><div className="selected-files__heading"><strong>{files.length} {files.length === 1 ? 'file' : 'files'} selected</strong><button type="button" onClick={() => setFiles([])}>Clear all</button></div>
        <ul>{files.map((file, index) => <li key={`${file.name}-${file.lastModified}-${index}`}><Icon name="film" size={20} /><span><strong>{file.name}</strong><small>{formatBytes(file.size)}</small></span><button type="button" onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove ${file.name}`}><Icon name="close" size={17} /></button></li>)}</ul>
      </div>}
    </> : <div className="upload-queue" aria-live="polite">
      {recoveryError && <div className="upload-recovery-error" role="alert"><span>{recoveryError}</span><button type="button" onClick={() => void retryPendingRecovery()} disabled={recoveringUploads}>{recoveringUploads ? 'Checking…' : 'Try again'}</button></div>}
      <div className="upload-queue__summary"><span>{items.filter((item) => item.state === 'complete').length} of {items.length} uploaded</span><strong>{hasPendingReview ? 'Waiting for duplicate review' : running ? 'Uploading with automatic retries…' : items.some((item) => item.state === 'failed') ? 'Finished with errors' : 'Uploads complete'}</strong></div>
      <ul>{items.map((item) => {
        const duplicate = item.uploadSession?.duplicateVideo;
        const duplicateDate = duplicate ? formatUploadDate(duplicate.createdAt) : '';
        const reviewDescriptionId = `duplicate-review-${item.id}`;
        return <li key={item.id} className={`upload-item upload-item--${item.state}`}><span className="upload-item__icon"><Icon name={item.state === 'complete' ? 'check' : ['failed', 'cancelled', 'duplicate'].includes(item.state) ? 'warning' : 'film'} size={19} /></span><div><div className="upload-item__name"><strong>{item.fileName}</strong><span>{['uploading', 'paused'].includes(item.state) ? `${item.progress}% ${item.state}` : item.state === 'duplicate' ? 'Review needed' : item.state}</span></div><div className="progress"><span style={{ width: `${item.progress}%` }} /></div>{item.state === 'duplicate' && <div className="upload-duplicate">
          <p id={reviewDescriptionId}>This uploaded content matches an existing video. Uploading another copy creates a separate video.</p>
          {duplicate && <div className="upload-duplicate__match"><span>Matching video</span><strong>{duplicate.title || 'Untitled video'}</strong>{(duplicate.sourceFilename || duplicate.sizeBytes !== null || duplicateDate) && <dl>{duplicate.sourceFilename && <><dt>Source</dt><dd>{duplicate.sourceFilename}</dd></>}{duplicate.sizeBytes !== null && <><dt>Size</dt><dd>{formatBytes(duplicate.sizeBytes)}</dd></>}{duplicateDate && <><dt>Added</dt><dd>{duplicateDate}</dd></>}</dl>}</div>}
          <div className="upload-item__actions upload-duplicate__actions"><button type="button" onClick={() => void decideDuplicate(item, 'continue')} disabled={Boolean(decidingUploadId)} aria-describedby={reviewDescriptionId} aria-busy={decidingUploadId === item.uploadSession?.id}>{decidingUploadId === item.uploadSession?.id ? <span className="spinner" /> : null}Upload another copy</button><button type="button" className="danger" onClick={() => void decideDuplicate(item, 'cancel')} disabled={Boolean(decidingUploadId)} aria-describedby={reviewDescriptionId}>Cancel this upload</button></div>
        </div>}{item.error && <small>{item.error}</small>}{activeItemRef.current === item.id && <div className="upload-item__actions">{item.state === 'paused' ? <button type="button" onClick={resumeActive}><Icon name="play" size={13} />Resume</button> : !imageAbortRef.current && <button type="button" onClick={() => void pauseActive()}><Icon name="pause" size={13} />Pause</button>}<button type="button" className="danger" onClick={() => void cancelActive()}><Icon name="close" size={13} />Cancel</button></div>}{item.state === 'failed' && localFilesRef.current.has(item.id) && <div className="upload-item__actions"><button type="button" onClick={() => void retryUpload(item)} disabled={running}><Icon name="refresh" size={13} />Retry</button></div>}</div></li>;
      })}</ul>
    </div> : importedVideo ? <div className={`import-status import-status--${importedVideo.status}`} aria-live="polite">
      <span className="import-status__icon">{importedVideo.status === 'ready' ? <Icon name="check" size={24} /> : importedVideo.status === 'failed' ? <Icon name="warning" size={24} /> : <span className="spinner spinner--large" />}</span>
      <div><span>{importedVideo.status === 'ready' ? 'Ready' : importedVideo.status === 'failed' ? 'Failed' : 'Importing from Adilo'}</span><strong>{friendlyImportStage(importedVideo)}</strong><p>{importedVideo.status === 'ready' ? 'The video is available in your library.' : importedVideo.status === 'failed' ? importedVideo.errorMessage || 'The source could not be imported.' : 'The server is copying this video. Closing this window will not stop the import.'}</p></div>
      {importedVideo.status !== 'failed' && <div className="import-status__progress"><div className="progress"><span style={{ width: `${importedVideo.progress}%` }} /></div><small>{Math.round(importedVideo.progress)}%</small></div>}
      {pollError && <p className="form-alert"><Icon name="warning" size={16} />{pollError}</p>}
    </div> : <form id="hls-import-form" className="dialog-form hls-import-form" onSubmit={(event) => void submitImport(event)}>
      {importError && <p className="form-alert"><Icon name="warning" size={16} />{importError}</p>}
      <label>Adilo HLS URL<input type="url" inputMode="url" required value={sourceUrl} onChange={(event) => { setSourceUrl(event.target.value); setImportError(''); }} placeholder="https://adilo.bigcommand.com/video-id.m3u8" autoComplete="url" /><small>Required. Use an HTTPS Adilo URL ending in .m3u8.</small></label>
      <label>Title <span className="field-optional">Optional</span><input type="text" value={importTitle} onChange={(event) => setImportTitle(event.target.value)} maxLength={255} placeholder="Video title" /></label>
      <label>Destination folder<select className="control-select" value={importFolderId ?? ''} onChange={(event) => setImportFolderId(event.target.value || null)}><option value="">Library root</option>{folderOptions.map((folder) => <option key={folder.id} value={folder.id}>{`${'— '.repeat(folder.depth)}${folder.name}`}</option>)}</select></label>
      <label>Thumbnail URL <span className="field-optional">Optional</span><input type="url" inputMode="url" value={thumbnailUrl} onChange={(event) => { setThumbnailUrl(event.target.value); setImportError(''); }} placeholder="https://example.com/thumbnail.jpg" autoComplete="url" /><small>Must be HTTPS when provided.</small></label>
    </form>}
  </Modal>;
}
